'use client';

import Link from 'next/link';
import { Reveal } from '@/components/products/Reveal';
import { CorporateDetailsSection } from '@/components/corporate/CorporateDetailsSection';
import { RoadmapSection } from '@/components/corporate/RoadmapSection';
import { company, addressLine } from '@/lib/company';
import { commercializationPath, exportCapability, roadmapDisclaimer } from '@/lib/corporate-content';

/**
 * /company — the corporate record page.
 *
 * This is the page a funder, a bank, a procurement reviewer or a prospective
 * partner opens to check that the entity is real and to understand its
 * structure. It is deliberately drier than /about-dilacode: facts, structure,
 * governance, how the business converts work into product, and what it does
 * not claim.
 */
export function CompanyPageClient() {
  return (
    <div className="self-styled">
      {/* ── Hero ── */}
      <section
        className="relative flex min-h-[70dvh] flex-col justify-center"
        style={{ backgroundColor: '#173835' }}
      >
        <div className="content-padding w-full pb-16 pt-40 md:pb-24 md:pt-48">
          <div className="pv-load">
            <span className="eyebrow">Corporate record</span>
          </div>
          <h1
            className="font-six-caps pv-load mt-8 max-w-[14ch] uppercase leading-[0.84] text-white"
            style={{ fontSize: 'clamp(3rem, 10vw, 9rem)', animationDelay: '80ms' }}
          >
            {company.legalName}
          </h1>
          <p
            className="pv-load mt-10 max-w-2xl text-base font-light leading-relaxed text-white/70 md:text-xl md:leading-relaxed"
            style={{ animationDelay: '160ms' }}
          >
            A corporation registered in {company.incorporatedIn}, corporation
            number {company.corporationNumber}, with its registered office at{' '}
            {addressLine}.
          </p>
        </div>
      </section>

      {/* The facts table */}
      <CorporateDetailsSection
        heading="At a glance"
        eyebrow="The record"
        id="corporate-record"
      />

      {/* ── What the company does ── */}
      <section
        className="relative z-20 border-b border-white/[0.07]"
        aria-labelledby="operations-heading"
      >
        <div className="content-padding">
          <div className="grid grid-cols-1 gap-10 py-24 md:py-32 lg:grid-cols-[minmax(0,22rem)_1fr] lg:gap-20">
            <Reveal>
              <span className="eyebrow">Operations</span>
              <h2
                id="operations-heading"
                className="font-six-caps mt-6 uppercase leading-[0.88] text-white"
                style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)' }}
              >
                What the
                <br />
                company does
              </h2>
            </Reveal>

            <Reveal delay={90}>
              <div className="flex max-w-3xl flex-col gap-6 text-base font-light leading-relaxed text-white/65 md:text-lg md:leading-relaxed">
                <p>
                  {company.legalName} builds and operates business software. We
                  develop our own products and also deliver software for clients.
                </p>
                <p>
                  Our products include a customer inbox and CRM, an ERP, and a
                  hospitality platform for digital menus, ordering and restaurant
                  management. All three are in active use.
                </p>
                <p>
                  Client work covers planning, design, engineering and release,
                  including publishing to the Apple App Store and Google Play.
                  The team has delivered projects in Iraq,
                  Georgia, the United Arab Emirates and the United Kingdom.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Governance and control ── */}
      <section
        className="relative z-20 border-b border-white/[0.07]"
        style={{ backgroundColor: 'rgba(10,10,10,0.72)' }}
        aria-labelledby="governance-heading"
      >
        <div className="content-padding">
          <div className="pb-4 pt-24 md:pt-32">
            <Reveal>
              <span className="eyebrow">Governance</span>
              <h2
                id="governance-heading"
                className="font-six-caps mt-6 max-w-4xl uppercase leading-[0.88] text-white"
                style={{ fontSize: 'clamp(2.25rem, 5vw, 4.5rem)' }}
              >
                How decisions and data are handled
              </h2>
            </Reveal>
          </div>

          <dl className="grid grid-cols-1 gap-x-16 pb-20 md:grid-cols-2">
            {[
              {
                term: 'Direction and accountability',
                def: `${company.contactName} is the founder and primary contact for the corporation. Enquiries about the company, its products or its record go to ${company.email}.`,
              },
              {
                term: 'Governing law',
                def: `Agreements with ${company.legalName} are governed by the laws of ${company.governingLaw}, and disputes are resolved in the courts of that jurisdiction.`,
              },
              {
                term: 'Intellectual property',
                def: 'The company owns the source, design and brands of its three software products. Ownership of client deliverables is set in each contract; reusable framework code remains with the company.',
              },
              {
                term: 'Customer data',
                def: 'Products can be cloud-hosted or self-hosted. Self-hosting lets an operator keep records in its own infrastructure when required.',
              },
              {
                term: 'Security posture',
                def: 'Products include access controls, tenant isolation and audit trails. The company does not currently hold a third-party security certification.',
              },
              {
                term: 'Subprocessors and hosting',
                def: 'Hosted deployments use managed cloud infrastructure. The providers used for a specific engagement are listed in its agreement.',
              },
            ].map((row, i) => (
              <Reveal key={row.term} delay={(i % 2) * 70}>
                <div className="flex flex-col gap-3 border-t border-white/10 py-8 md:py-9">
                  <dt className="text-base font-medium leading-snug text-white md:text-lg">
                    {row.term}
                  </dt>
                  <dd className="max-w-xl text-sm font-light leading-relaxed text-white/55 md:text-[15px]">
                    {row.def}
                  </dd>
                </div>
              </Reveal>
            ))}
          </dl>
        </div>
      </section>

      {/* ── Business model ── */}
      <section
        className="relative z-20 border-b border-white/[0.07]"
        aria-labelledby="model-heading"
      >
        <div className="content-padding">
          <div className="pb-4 pt-24 md:pt-32">
            <Reveal>
              <span className="eyebrow">Business model</span>
              <h2
                id="model-heading"
                className="font-six-caps mt-6 max-w-4xl uppercase leading-[0.88] text-white"
                style={{ fontSize: 'clamp(2.25rem, 5vw, 4.5rem)' }}
              >
                From engagement to owned product
              </h2>
              <p className="mt-8 max-w-2xl text-base font-light leading-relaxed text-white/60 md:text-lg md:leading-relaxed">
                DilaERP, DilaCRM and dila.menu each grew from practical work into
                a product with its own release cycle.
              </p>
            </Reveal>
          </div>

          <ol
            className="mt-12 grid grid-cols-1 gap-px pb-20 sm:grid-cols-2 lg:grid-cols-4"
            style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
          >
            {commercializationPath.map((step, i) => (
              <li key={step.index} className="flex" style={{ backgroundColor: '#0a0a0a' }}>
                <Reveal delay={i * 70} className="w-full">
                  <div className="flex h-full flex-col gap-4 p-6 md:p-8">
                    <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/35">
                      {step.index}
                    </span>
                    <h3 className="text-base font-medium leading-snug text-white">
                      {step.title}
                    </h3>
                    <p className="text-sm font-light leading-relaxed text-white/55">
                      {step.body}
                    </p>
                  </div>
                </Reveal>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Export readiness ── */}
      <section
        className="relative z-20 border-b border-white/[0.07]"
        style={{ backgroundColor: 'rgba(10,10,10,0.72)' }}
        aria-labelledby="export-heading"
      >
        <div className="content-padding">
          <div className="grid grid-cols-1 gap-10 pt-24 md:pt-32 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
            <Reveal>
              <span className="eyebrow">{exportCapability.eyebrow}</span>
              <h2
                id="export-heading"
                className="font-six-caps mt-6 uppercase leading-[0.88] text-white"
                style={{ fontSize: 'clamp(2.25rem, 5vw, 4.5rem)' }}
              >
                {exportCapability.title}
              </h2>
            </Reveal>
            <Reveal delay={90}>
              <p className="max-w-xl text-base font-light leading-relaxed text-white/60 md:text-lg md:leading-relaxed">
                {exportCapability.body}
              </p>
            </Reveal>
          </div>

          <dl className="mb-24 mt-14 grid grid-cols-1 gap-x-10 sm:grid-cols-2 lg:grid-cols-4">
            {exportCapability.facts.map((fact, i) => (
              <Reveal key={fact.label} delay={i * 60}>
                <div className="flex flex-col gap-3 border-t border-white/10 py-7">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">
                    {fact.label}
                  </dt>
                  <dd className="text-base font-light leading-snug text-white/85">
                    {fact.value}
                  </dd>
                </div>
              </Reveal>
            ))}
          </dl>
        </div>
      </section>

      {/* Roadmap themes — shared with the homepage and About */}
      <RoadmapSection />

      {/* ── What we do not claim ── */}
      <section
        className="relative z-20 border-b border-white/[0.07]"
        style={{ backgroundColor: 'rgba(10,10,10,0.72)' }}
        aria-labelledby="not-claimed-heading"
      >
        <div className="content-padding">
          <div className="grid grid-cols-1 gap-10 py-24 md:py-32 lg:grid-cols-[minmax(0,22rem)_1fr] lg:gap-20">
            <Reveal>
              <span className="eyebrow">Stated plainly</span>
              <h2
                id="not-claimed-heading"
                className="font-six-caps mt-6 uppercase leading-[0.88] text-white"
                style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)' }}
              >
                What we
                <br />
                do not claim
              </h2>
            </Reveal>
            <Reveal delay={90}>
              <div className="flex max-w-3xl flex-col gap-6 text-base font-light leading-relaxed text-white/65 md:text-lg md:leading-relaxed">
                <p>
                  We publish no revenue figures, no headcount, no private customer
                  information and no certifications the company does not hold.
                  Earlier studio projects are labelled separately from work by the
                  Canadian corporation.
                </p>
                <p>{roadmapDisclaimer}</p>
                <p className="text-white/50">
                  For due-diligence questions, contact{' '}
                  <a
                    href={`mailto:${company.email}`}
                    className="text-white underline decoration-white/30 underline-offset-4 transition-colors hover:decoration-white"
                  >
                    {company.email}
                  </a>.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <Link href="/contact" className="block group" style={{ backgroundColor: '#173835' }}>
        <section
          className="flex cursor-pointer flex-col items-center justify-center"
          style={{ paddingTop: 'clamp(6rem, 12vw, 12rem)', paddingBottom: 'clamp(6rem, 12vw, 12rem)' }}
        >
          <p
            className="text-center uppercase transition-opacity duration-500 group-hover:opacity-80"
            style={{ fontSize: '11px', letterSpacing: '0.25em', color: 'rgba(255,255,255,0.4)', marginBottom: 'clamp(1rem, 2vw, 2rem)' }}
          >
            Company enquiries
          </p>
          <h2
            className="font-six-caps text-white lowercase transition-transform duration-500 group-hover:scale-[1.02]"
            style={{ fontSize: 'clamp(5rem, 20vw, 18rem)', lineHeight: 0.85 }}
          >
            CONTACT
          </h2>
        </section>
      </Link>
    </div>
  );
}
